/**
 * Vocabulaire des événements, côté interface.
 *
 * L'interface ne tient pas la liste de référence des types : celle-ci vit dans
 * le registre du serveur, seul juge à l'écriture, et la base en porte une
 * contrainte de vérification confrontée à ce registre au démarrage. Ce fichier
 * ne déclare que ce qui est propre à l'affichage — les libellés — et le
 * sous-ensemble de types que cet écran sait écrire, qui est un choix
 * d'interface et non une liste d'autorisation.
 */

/** Types que l'interface sait déclarer par un formulaire générique. */
export const MANUAL_EVENT_TYPES = [
  'changementProprietaire',
  'changementEtat',
  'intervention',
  'miseEnService',
  'miseAuRebut',
] as const;

export type ManualEventType = (typeof MANUAL_EVENT_TYPES)[number];

/**
 * Tout ce que l'interface peut écrire au journal. Le changement de pièce a son
 * propre formulaire, qui doit choisir une référence puis un exemplaire ou une
 * quantité : il ne rentre pas dans le formulaire générique.
 */
export type WritableEventType =
  | ManualEventType
  | 'annulation'
  | 'changementPiece'
  | 'regularisationStock';

/** Types dont le motif ne peut pas rester vide, comme côté serveur. */
const REASON_REQUIRED: readonly WritableEventType[] = [
  'changementEtat',
  'changementPiece',
  'regularisationStock',
  'annulation',
];

export function reasonRequiredFor(type: WritableEventType): boolean {
  return REASON_REQUIRED.includes(type);
}

/**
 * Table de libellés, pas une liste d'autorisation : un type que cette version
 * de l'interface ne connaît pas s'affiche sous son identifiant plutôt que de
 * disparaître de la frise.
 */
const EVENT_TYPE_LABELS: Record<string, string> = {
  importDiagnostic: 'Import de diagnostic',
  changementProprietaire: 'Changement de propriétaire',
  changementEtat: "Changement d'état",
  changementPiece: 'Changement de pièce',
  intervention: 'Intervention',
  miseEnService: 'Mise en service',
  miseAuRebut: 'Mise au rebut',
  regularisationStock: 'Régularisation de stock',
  annulation: 'Annulation',
};

export function eventTypeLabel(type: string): string {
  return EVENT_TYPE_LABELS[type] ?? type;
}

/**
 * Nature de la valeur qu'un événement fait bouger.
 *
 * Sert uniquement à avertir l'utilisateur qu'un événement antidaté ne
 * changera pas la valeur courante. Le calcul qui fait autorité est celui du
 * serveur ; cette table ne décide de rien.
 */
export type EventNature = 'etat' | 'proprietaire';

const NATURE_BY_TYPE: Record<string, EventNature> = {
  changementEtat: 'etat',
  miseEnService: 'etat',
  miseAuRebut: 'etat',
  changementProprietaire: 'proprietaire',
};

export function natureOf(type: string): EventNature | null {
  return NATURE_BY_TYPE[type] ?? null;
}

export const NATURE_LABELS: Record<EventNature, string> = {
  etat: "l'état courant du poste",
  proprietaire: 'le propriétaire courant du poste',
};

export const INTERVENTION_CATEGORIES = [
  'materiel',
  'logiciel',
  'reseau',
  'nettoyage',
  'autre',
] as const;

export type InterventionCategory = (typeof INTERVENTION_CATEGORIES)[number];

const INTERVENTION_CATEGORY_LABELS: Record<string, string> = {
  materiel: 'Matériel',
  logiciel: 'Logiciel',
  reseau: 'Réseau',
  nettoyage: 'Nettoyage',
  autre: 'Autre',
};

export function interventionCategoryLabel(category: string): string {
  return INTERVENTION_CATEGORY_LABELS[category] ?? category;
}
